// ==========================================================================
// FILE: /app/components/onboarding/steps/BeneficiaryStep.tsx
// ==========================================================================
import { useState, type FC } from "react";
import EmailInput from "@/app/components/common/actions/inputs-b/EmailInput";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import BeneficiaryName from "@/app/components/common/actions/inputs-b/BeneficiaryName";
import NickName from "@/app/components/common/actions/inputs-b/NickName";
import BeneficiaryAddress from "@/app/components/common/actions/inputs-b/BeneficiaryAddress";
import BeneficiaryBankCountry from "@/app/components/common/actions/inputs-b/BeneficiaryBankCountry";
import BeneficiaryAccount from "@/app/components/common/actions/inputs-b/BeneficiaryAccount";
import type { BeneficiaryFormData } from "./types";
import { GxLineStepper, type GxLineStepperStep } from "@/app/components/common/actions/Stepper/GxLineStepper";
 
interface BeneficiaryStepProps {
  data: BeneficiaryFormData;
  updateField: (field: keyof BeneficiaryFormData, value: any) => void;
  subStep: 0 | 1;
  setSubStep: (step: 0 | 1) => void;
}
 
const BENEFICIARY_SUBSTEPS: GxLineStepperStep[] = [
  { id: "beneficiary", label: "Beneficiary Details" },
  { id: "account", label: "Account Details" },
];
 
const BeneficiaryStep: FC<BeneficiaryStepProps> = ({ data, updateField, subStep, setSubStep }) => {
  const [mode, setMode] = useState<"swift" | "addManually">("swift");
 
  return (
   
      <div className="gx-bene-form-container-content gx-bene-form-container-content--active">
        <GxLineStepper steps={BENEFICIARY_SUBSTEPS} currentStep={subStep} showStepCounter  barHeight={10} />
 
        {subStep === 0 && (
          <>
            <div className="gx-dialog-title-h3">
              <h3 className="gx-h3-b">Beneficiary Details</h3>
            </div>
 
            <div className="flex flex-wrap -mx-2 gx-row-bottom">
              <div className="w-full md:w-1/2 px-2">
                <BeneficiaryName
                  fieldName="beneficiaryName"
                  label="Beneficiary Name"
                  value={data.name}
                  onChange={(value: string) => updateField("name", value)}
                  onValidate={() => {}}
                  triggerValidation={false}
                />
              </div>
              <div className="w-full md:w-1/2 px-2">
                <NickName
                  label="Nick Name"
                  value={data.nickName}
                  onChange={(value: string) => updateField("nickName", value)}
                  onValidate={() => {}}
                  triggerValidation={false}
                />
              </div>
            </div>
 
            <div className="flex flex-wrap -mx-2 gx-row-bottom">
              <div className="w-full md:w-1/2 px-2">
                <EmailInput
                  label="Email ID"
                  value={data.email}
                  onChange={(value: string) => updateField("email", value)}
                  onValidate={() => {}}
                  triggerValidation={false}
                />
              </div>
              <div className="w-full md:w-1/2 px-2">
                <Mobilefiled
                  label="Mobile No"
                  value={data.mobile}
                  onChange={(val: string) => updateField("mobile", val)}
                  onValidate={() => {}}
                  countryDialCode={data.countryDialCode}
                  onCountryCodeChange={(dialCode: string) => updateField("countryDialCode", dialCode)}
                  triggerValidation={false}
                  disable={false}
                />
              </div>
            </div>
 
            <div className="flex flex-wrap -mx-2 gx-row-bottom">
              <div className="w-full md:w-1/2 px-2">
                <BeneficiaryAddress
                  label="Beneficiary Address"
                  value={data.address}
                  onChange={(value: string) => updateField("address", value)}
                  onValidate={() => {}}
                  triggerValidation={false}
                  minLength={4}
                />
              </div>
            </div>
          </>
        )}
 
        {subStep === 1 && (
          <>
            <button className="gx-p12-sb text-gray-500 mb-2" onClick={() => setSubStep(0)}>
              ← Back to Beneficiary Details
            </button>
 
            <div className="gx-dialog-title-h3">
              <h3 className="gx-h3-b">Account Details</h3>
            </div>
 
            <div className="flex flex-wrap -mx-2 gx-row-bottom">
              <div className="w-full md:w-1/2 px-2">
                <BeneficiaryBankCountry
                  label="Beneficiary Bank Country"
                  countries={[]}
                  value={data.bankCountry}
                  onChange={(country: string) => updateField("bankCountry", country)}
                  onValidate={() => {}}
                  triggerValidation={false}
                />
              </div>
              <div className="w-full md:w-1/2 px-2">
                <BeneficiaryAccount
                  label="Beneficiary Account / IBAN Number"
                  value={data.account}
                  onChange={(value: string) => updateField("account", value)}
                  ibanRequired={false}
                  onValidate={() => {}}
                  triggerValidation={false}
                />
              </div>
            </div>
 
            <div className="flex items-center gx-bene-or-content">
              <div className="grow border-t border-gray-300" />
              <span className="px-4 text-gray-500 text-sm">or</span>
              <div className="grow border-t border-gray-300" />
            </div>
 
            <div className="text-center mt-4">
              <button
                onClick={() => setMode((m) => (m === "swift" ? "addManually" : "swift"))}
                className="gx-p12-m cursor-pointer text-blue-500"
              >
                {mode === "addManually" ? "+ Search Bank" : "+ Add Manually"}
              </button>
            </div>
          </>
        )}
      </div>
   
  );
};
 
export default BeneficiaryStep;